import { useCallback } from 'react';

import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { useCanvas } from '../../../../core/components/canvas/hooks/useCanvas.js';
import { CanvasTextAreaProps } from '../../../types/canvas-text-area.js';
import { drawLabelAndValue } from '../utils/draw-label-and-value.js';
import { fillShape } from '../utils/fill-shape.js';
import { removeTransparentBorder } from '../utils/remove-transparent-border.js';

interface CanvasCircleRendererProps {
  x: number;
  y: number;
  radius: number;
  fillColor: string;
  value: string | number;
  name?: string;
  strokeColor?: 'transparent' | string;
  showLabels?: boolean;
  truncationMode?: TruncationMode;
}

const createCirclePath = (
  ctx: CanvasRenderingContext2D,
  radius: number,
  x: number,
  y: number,
) => {
  ctx.beginPath();
  const safeRadius = Math.max(radius, 0);
  ctx.arc(x, y, safeRadius, 0, 2 * Math.PI);
  ctx.closePath();
};

const getCircleTextArea = (x: number, y: number, squareSideSize: number) => ({
  x,
  y,
  width: squareSideSize,
  height: squareSideSize - 8,
  leftBoundary: x - squareSideSize / 2,
  rightBoundary: x + squareSideSize / 2,
});

const getCircumscribedSquare = (radius: number) => radius * Math.sqrt(2);

export const CanvasCircleRenderer = (props: CanvasCircleRendererProps) => {
  const {
    x,
    y,
    radius,
    fillColor,
    strokeColor = fillColor,
    value,
    name = '',
    showLabels,
    truncationMode,
  } = props;
  const squareSideSize = getCircumscribedSquare(radius);
  const textArea: CanvasTextAreaProps = getCircleTextArea(x, y, squareSideSize);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      createCirclePath(ctx, radius, x, y);
      removeTransparentBorder(ctx, strokeColor);
      fillShape(ctx, fillColor);
      if (showLabels) {
        drawLabelAndValue(
          ctx,
          value,
          textArea,
          fillColor,
          name,
          truncationMode,
        );
      }

      return false;
    },
    [
      x,
      y,
      radius,
      fillColor,
      strokeColor,
      showLabels,
      truncationMode,
      name,
      value,
    ],
  );

  useCanvas('honeycomb-circle', draw);

  return null;
};

CanvasCircleRenderer['displayName'] = 'CanvasCircleRenderer';
